import { Tab } from '@mui/material';
import { FC, Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { generatePath } from 'react-router';
import { useNavigate } from 'react-router-dom';

import { tradersApi } from 'api';
import { PageHeader, QueryTabs, QueryTabPanel, DataWrapper } from 'components';
import { NEW_ID } from 'constants/common.constants';
import { ROUTE_PATH } from 'constants/routes';
import { PayoutRequisitesTabs, QueryKey } from 'enums';
import { useQueryTab, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';

import { PayoutRequisitesAutomationList } from '../RequisitesAutomation';
import { RequisitesBalanceRedistributionDialog } from '../RequisitesBalanceRedistributionDialog';
import { PayoutRequisitesBalanceRedistributionList } from '../RequisitesBalanceRedistributionList';
import { PayoutRequisitesLimitsList } from '../RequisitesLimitsList';
import { PayoutRequisitesList } from '../RequisitesList';

export const PayoutRequisitesPage: FC = () => {
  const { tab } = useQueryTab<PayoutRequisitesTabs>();

  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.requisites',
  });

  const { isTrader, isAdminOrTechOperator, isManager } = useUser();

  const tradeInfoQueryResult = useQuery(QueryKey.MyInfo, tradersApi.getMyInfo, {
    enabled: isTrader,
  });

  const [
    requisitesBalanceRedistributionDialogOpen,
    setRequisitesBalanceRedistributionDialogOpen,
  ] = useState(false);

  const canSeeAutomation = useMemo(
    () =>
      isManager ||
      (isTrader &&
        tradeInfoQueryResult.data?.payoutRequisitesAutomationEnabled),
    [
      isManager,
      isTrader,
      tradeInfoQueryResult.data?.payoutRequisitesAutomationEnabled,
    ],
  );

  const canSeeLimits = useMemo(
    () => isTrader || isAdminOrTechOperator,
    [isAdminOrTechOperator, isTrader],
  );

  const canSeeBalanceRedistribution = useMemo(
    () => canSeeAutomation && (isTrader || isAdminOrTechOperator),
    [isAdminOrTechOperator, isTrader, canSeeAutomation],
  );

  const canCreate = useMemo(() => {
    if (tab === PayoutRequisitesTabs.Limits) {
      return false;
    } else if (tab === PayoutRequisitesTabs.BalanceRedistribution) {
      return isAdminOrTechOperator || isTrader;
    }
    return isTrader;
  }, [isTrader, isAdminOrTechOperator, tab]);

  const navigate = useNavigate();

  const handleCreateRequisites = useCallback(() => {
    navigate(
      generatePath(ROUTE_PATH.TRADER.PAYOUT_REQUISITES_DETAILS, {
        id: NEW_ID,
      }),
    );
  }, [navigate]);

  const handleCreateAutomation = useCallback(() => {
    navigate(
      generatePath(ROUTE_PATH.TRADER.PAYOUT_REQUISITES_AUTOMATION_DETAILS, {
        id: NEW_ID,
      }),
    );
  }, [navigate]);

  const handleCreateClick = useCallback(() => {
    if (tab === PayoutRequisitesTabs.Requisites) {
      handleCreateRequisites();
    } else if (tab === PayoutRequisitesTabs.Automation) {
      handleCreateAutomation();
    } else if (tab === PayoutRequisitesTabs.BalanceRedistribution) {
      setRequisitesBalanceRedistributionDialogOpen(true);
    }
  }, [tab, handleCreateRequisites, handleCreateAutomation]);

  const handleRequisitesBalanceRedistributionDialogClose = useCallback(() => {
    setRequisitesBalanceRedistributionDialogOpen(false);
  }, []);

  return (
    <Fragment>
      <PageHeader
        title={t('title')}
        divider={false}
        {...(canCreate && {
          rightContentButton: { onClick: handleCreateClick },
        })}
      />
      <DataWrapper
        isLoading={
          tradeInfoQueryResult.isLoading || tradeInfoQueryResult.isRefetching
        }
      >
        <Fragment>
          <QueryTabs tabsEnum={PayoutRequisitesTabs}>
            <Tab
              value={PayoutRequisitesTabs.Requisites}
              label={t('tabs.requisites')}
            ></Tab>
            {canSeeLimits && (
              <Tab
                value={PayoutRequisitesTabs.Limits}
                label={t('tabs.limits')}
              ></Tab>
            )}
            {canSeeAutomation && (
              <Tab
                value={PayoutRequisitesTabs.Automation}
                label={t('tabs.automation')}
              ></Tab>
            )}
            {canSeeBalanceRedistribution && (
              <Tab
                value={PayoutRequisitesTabs.BalanceRedistribution}
                label={t('tabs.balance_redistribution')}
              ></Tab>
            )}
          </QueryTabs>
          <QueryTabPanel value={PayoutRequisitesTabs.Requisites}>
            <PayoutRequisitesList />
          </QueryTabPanel>
          {canSeeLimits && (
            <QueryTabPanel value={PayoutRequisitesTabs.Limits}>
              <PayoutRequisitesLimitsList />
            </QueryTabPanel>
          )}
          {canSeeAutomation && (
            <QueryTabPanel value={PayoutRequisitesTabs.Automation}>
              <PayoutRequisitesAutomationList />
            </QueryTabPanel>
          )}
          {canSeeBalanceRedistribution && (
            <QueryTabPanel value={PayoutRequisitesTabs.BalanceRedistribution}>
              <PayoutRequisitesBalanceRedistributionList />
            </QueryTabPanel>
          )}
        </Fragment>
      </DataWrapper>
      <RequisitesBalanceRedistributionDialog
        open={requisitesBalanceRedistributionDialogOpen}
        onClose={handleRequisitesBalanceRedistributionDialogClose}
      />
    </Fragment>
  );
};
